import {
    Paragraph,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    VerticalAlign,
    AlignmentType,
    TextRun,
    TableLayoutType,
    UnderlineType,
    HorizontalPositionRelativeFrom,
    VerticalPositionRelativeFrom,
} from "docx";
import {
    FULL_WIDTH,
    SCALE,
    fixColor,
    fitImage,
    fetchImageWithDimensions,
    drawPlaceholderIcon,
    drawOrgText,
    buildOrgTree,
    getMaxOrgDepth,
    getStripeRun,
    createPageNumberRun,
    fitFloatingImage,
    getContrastColor
} from "@/utils/wordGenerator/common.js";
import {api_baseURL} from "@/utils/init.js";

// --- Helper: Generate Org Chart Image Data (Circular Nodes) ---
const generateOrgChartImage = async (orgChart) => {
    const treeData = buildOrgTree(orgChart.members);
    if (treeData.length === 0) return null;

    const NODE_SIZE = 110; // Circular diameter
    const NODE_WIDTH = 120; // Width for subtree calculation
    const TEXT_AREA_HEIGHT = 60;
    const NODE_HEIGHT = NODE_SIZE + TEXT_AREA_HEIGHT;
    const GAP_X = 20;

    const getSubtreeWidth = (node) => {
        if (!node.children || node.children.length === 0) return NODE_WIDTH;
        const childrenW = node.children.reduce((acc, c) => acc + getSubtreeWidth(c), 0);
        const childrenGap = (node.children.length - 1) * GAP_X;
        return Math.max(NODE_WIDTH, childrenW + childrenGap);
    };

    const maxDepth = getMaxOrgDepth(treeData);
    const canvasTargetHeight = 1100;
    const totalNodesHeight = maxDepth * NODE_HEIGHT;
    const availableGapSpace = Math.max(50, canvasTargetHeight - totalNodesHeight - 150);
    const LEVEL_GAP = maxDepth > 1 ? Math.min(80, availableGapSpace / (maxDepth - 1)) : 80;

    const totalWidth = treeData.reduce((acc, r) => acc + getSubtreeWidth(r), 0) + (treeData.length - 1) * GAP_X;
    const totalHeight = Math.max(canvasTargetHeight, totalNodesHeight + (maxDepth - 1) * LEVEL_GAP + 100);

    const canvas = document.createElement('canvas');
    const DPR = 3;
    canvas.width = (totalWidth + 100) * DPR;
    canvas.height = totalHeight * DPR;
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, totalWidth + 100, totalHeight);

    const labelStyle = orgChart.style.label || {};
    const textStyle = orgChart.style.text || {};
    const bodyColor = fixColor(orgChart.style.colorScheme?.body || "4a4a4a");

    const drawNode = async (node, x, y, w) => {
        const nodeX = x + (w - NODE_SIZE) / 2;
        const nodeY = y;
        const centerX = nodeX + NODE_SIZE / 2;
        const centerY = nodeY + NODE_SIZE / 2;

        // 1. Photo (Circular Clip)
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, NODE_SIZE / 2, 0, Math.PI * 2);
        ctx.clip();

        if (node.profilePicturePath) {
            const imgUrl = `${api_baseURL}profilePicture?tmid=${node.teamMemberId}`;
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.src = imgUrl;
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });
                ctx.drawImage(img, nodeX, nodeY, NODE_SIZE, NODE_SIZE);
            } catch (e) {
                drawPlaceholderIcon(ctx, nodeX, nodeY, NODE_SIZE);
            }
        } else {
            drawPlaceholderIcon(ctx, nodeX, nodeY, NODE_SIZE);
        }
        ctx.restore();

        // 2. Text Details (Centered Below)
        drawOrgText(ctx, {
            name: node.teamMemberName || "N/A",
            role: node.role || "N/A",
            nameStyle: labelStyle,
            roleStyle: textStyle,
            x: centerX,
            y: nodeY + NODE_SIZE + 8,
            width: NODE_WIDTH,
            contrastColor: "#" + fixColor(getContrastColor(bodyColor))
        });

        // 3. Connectors
        if (node.children && node.children.length > 0) {
            const subtreeWidths = node.children.map(getSubtreeWidth);
            const childrenTotalW = subtreeWidths.reduce((a, b) => a + b, 0) + (node.children.length - 1) * GAP_X;
            let currentX = x + (w - childrenTotalW) / 2;
            const childY = y + NODE_HEIGHT + LEVEL_GAP;

            ctx.strokeStyle = "#888888";
            ctx.lineWidth = 1.5;

            // vertical down from parent
            ctx.beginPath();
            ctx.moveTo(centerX, nodeY + NODE_HEIGHT);
            ctx.lineTo(centerX, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.stroke();

            // horizontal bar
            const firstChildCenter = currentX + subtreeWidths[0] / 2;
            const lastChildCenter = currentX + (childrenTotalW - subtreeWidths[subtreeWidths.length - 1] / 2);
            ctx.beginPath();
            ctx.moveTo(firstChildCenter, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.lineTo(lastChildCenter, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
            ctx.stroke();

            for (let i = 0; i < node.children.length; i++) {
                const childW = subtreeWidths[i];
                const childX = currentX + childW / 2;
                ctx.beginPath();
                ctx.moveTo(childX, nodeY + NODE_HEIGHT + LEVEL_GAP / 2);
                ctx.lineTo(childX, childY);
                ctx.stroke();
                await drawNode(node.children[i], currentX, childY, childW);
                currentX += childW + GAP_X;
            }
        }
    };

    let startX = 50;
    for (const root of treeData) {
        const w = getSubtreeWidth(root);
        await drawNode(root, startX, 40, w);
        startX += w + GAP_X;
    }

    const blob = await new Promise(r => canvas.toBlob(r, 'image/png', 1.0));
    const arrayBuffer = await blob.arrayBuffer();
    return {
        data: new Uint8Array(arrayBuffer),
        width: canvas.width / DPR,
        height: canvas.height / DPR
    };
};

export const createOrgChartTemp3 = async (orgData, startPage) => {
    const sections = [];
    const style = orgData.style || {};
    const rawBoxColor = fixColor(style.colorScheme?.body || "EEEEEE");
    const rawHeaderColor = fixColor(style.colorScheme?.header || "4a4a4a");
    const rawFooterColor = fixColor(style.colorScheme?.footer || "4a4a4a");
    const headingStyle = style.heading || {};

    const leftStripeRun = getStripeRun("left", '#bec6d4');

    const HEADER_HEIGHT = 800;
    const BOTTOM_SECTION_HEIGHT = 4000;
    const BODY_HEIGHT = 12030;

    // --- Header Row ---
    const headerRow = new TableRow({
        height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
        children: [
            new TableCell({
                columnSpan: 3,
                verticalAlign: VerticalAlign.CENTER,
                children: [
                    new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        layout: TableLayoutType.FIXED,
                        borders: {
                            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                        },
                        rows: [
                            new TableRow({
                                verticalAlign: VerticalAlign.CENTER,
                                height: { value: HEADER_HEIGHT, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        width: { size: 30, type: WidthType.PERCENTAGE },
                                        shading: { fill: rawHeaderColor },
                                        verticalAlign: VerticalAlign.CENTER,
                                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [leftStripeRun] })]
                                    }),
                                    new TableCell({
                                        width: { size: 70, type: WidthType.PERCENTAGE },
                                        margins: { top: 0, bottom: 0, right: 0, left: 1200 },
                                        verticalAlign: VerticalAlign.CENTER,
                                        children: [
                                            new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: orgData.title,
                                                        color: rawHeaderColor,
                                                        bold: true,
                                                        size: 18 * SCALE,
                                                        font: "sans-serif"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });

    // --- Body Row (Chart) ---
    const chartImgData = await generateOrgChartImage(orgData);
    const chartRun = chartImgData ? fitImage(chartImgData, FULL_WIDTH * 0.95, BODY_HEIGHT * 0.9, "contain") : null;

    const bodyRow = new TableRow({
        height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
        children: [
            new TableCell({
                columnSpan: 3,
                verticalAlign: VerticalAlign.CENTER,
                children: chartRun ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [chartRun] })] : [new Paragraph("No chart data")]
            })
        ]
    });

    // --- Bottom Section Row (Editable Text + Project Image) ---
    let projectImgRun = null;
    const projectPhoto = orgData.projects?.[0]?.uploadedPhoto || orgData.projects?.[0]?.photoURL;
    if (projectPhoto) {
        try {
            const imgData = await fetchImageWithDimensions(projectPhoto);
            projectImgRun = fitFloatingImage(imgData, FULL_WIDTH * 0.35, BOTTOM_SECTION_HEIGHT, {
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    offset: 7739,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PAGE,
                    offset: 13000,
                },
                zIndex: 0,
                behindDocument: true,
            });
        } catch (e) {
            console.error("Failed to fetch project image for OrgChartTemp3", e);
        }
    }

    const bottomRow = new TableRow({
        height: { value: BOTTOM_SECTION_HEIGHT, rule: HeightRule.EXACT },
        children: [
            new TableCell({
                width: { size: 63, type: WidthType.PERCENTAGE },
                shading: { fill: rawFooterColor },
                margins: { left: 200, right: 200 },
                children: [
                    new Paragraph({
                        spacing: { before: 200 },
                        children: [
                            new TextRun({
                                text: orgData.about?.title,
                                color: fixColor(headingStyle.color || "000000"),
                                bold: headingStyle.bold ?? true,
                                italics: headingStyle.italic ?? false,
                                underline: headingStyle.underline ? { type: UnderlineType.SINGLE } : undefined,
                                size: (headingStyle.size || 16) * SCALE,
                                font: headingStyle.font || "sans-serif"
                            })
                        ]
                    }),
                    new Paragraph({
                        spacing: { before: 200, after: 200 },
                        children: [
                            new TextRun({
                                text: orgData.about?.description,
                                color: fixColor(headingStyle.color || "000000"),
                                italics: headingStyle.italic ?? false,
                                underline: headingStyle.underline ? { type: UnderlineType.SINGLE } : undefined,
                                size: Math.floor((headingStyle.size || 16) / 1.7) * SCALE,
                                font: headingStyle.font || "sans-serif"
                            })
                        ]
                    })
                ]
            }),
            new TableCell({
                width: { size: 2, type: WidthType.PERCENTAGE },
                children: []
            }),
            new TableCell({
                width: { size: 35, type: WidthType.PERCENTAGE },
                verticalAlign: VerticalAlign.TOP,
                shading: projectImgRun ? undefined : { fill: rawFooterColor },
                children: [
                    new Paragraph({
                        children: [
                            ...(projectImgRun ? [projectImgRun] : []),
                        ]
                    }),
                    new Paragraph({
                        alignment: AlignmentType.RIGHT,
                        indent: { right: 200 },
                        spacing: { before: 3400 },
                        children: [
                            createPageNumberRun(startPage),
                        ]
                    })
                ]
            })
        ]
    });

    const mainTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [headerRow, bodyRow, bottomRow]
    });

    sections.push({
        properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
        children: [mainTable]
    });

    return sections;
};
